import React, { useState, useEffect } from "react";
import axios from "axios";
import { buildApiUrl } from "../config/api.config";
import AIBackgroundEffects from "./AIBackgroundEffects";
import "./EmailManager.css";

const EmailManager = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, sent, failed, pending
  const [typeFilter, setTypeFilter] = useState("all"); // all, welcome, agent_ready, etc.
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [resending, setResending] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [emailToResend, setEmailToResend] = useState(null);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    fetchEmails();
  }, []);

  const fetchEmails = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await axios.get(buildApiUrl("/api/admin/emails"), {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setEmails(response.data.data);
      }
    } catch (error) {
      console.error("Error al cargar correos:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewEmail = (email) => {
    setSelectedEmail(email);
  };

  const handleCloseModal = () => {
    setSelectedEmail(null);
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const handleResendEmailRequest = (emailId) => {
    setEmailToResend(emailId);
    setShowConfirmModal(true);
  };

  const handleConfirmResend = async () => {
    setShowConfirmModal(false);
    setResending(true);

    try {
      const token = localStorage.getItem("adminToken");
      const response = await axios.post(
        buildApiUrl(`/api/admin/emails/${emailToResend}/resend`),
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        showNotification("✓ Correo reenviado exitosamente", "success");
        fetchEmails(); // Recargar lista
        handleCloseModal();
      } else {
        showNotification("❌ Error al reenviar: " + response.data.message, "error");
      }
    } catch (error) {
      console.error("Error al reenviar correo:", error);
      showNotification("❌ Error al reenviar correo", "error");
    } finally {
      setResending(false);
      setEmailToResend(null);
    }
  };

  const handleCancelResend = () => {
    setShowConfirmModal(false);
    setEmailToResend(null);
  };

  const getStatusLabel = (status) => {
    const labels = {
      sent: "✓ Enviado",
      failed: "❌ Fallido",
      pending: "⏳ Pendiente",
    };
    return labels[status] || status;
  };

  const getTypeLabel = (type) => {
    const labels = {
      welcome: "👋 Bienvenida",
      agent_ready: "🎉 Agente Listo",
      payment_confirmation: "💳 Confirmación Pago",
      subscription_cancelled: "❌ Cancelación",
      custom: "📧 Personalizado",
    };
    return labels[type] || type;
  };

  const filteredEmails = emails.filter((email) => {
    if (filter !== "all" && email.status !== filter) return false;
    if (typeFilter !== "all" && email.type !== typeFilter) return false;
    if (
      searchTerm &&
      !email.to.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !email.businessName.toLowerCase().includes(searchTerm.toLowerCase())
    ) {
      return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="email-loading">
        <div className="email-manager-loading-spinner"></div>
        <p>Cargando correos...</p>
      </div>
    );
  }

  return (
    <div className="email-manager">
      <AIBackgroundEffects step={1} withSidebar={true} />
      {/* Header */}
      <div className="email-header">
        <div>
          <h1>📧 Gestor de Correos</h1>
          <p className="email-manager-header-subtitle">
            Historial de emails enviados del sistema
          </p>
        </div>
        <button className="email-manager-refresh-btn" onClick={fetchEmails}>
          🔄 Actualizar
        </button>
      </div>

      {/* Filters */}
      <div className="email-filters">
        <div className="email-manager-search-box">
          <span className="email-manager-search-icon">🔍</span>
          <input
            type="text"
            placeholder="Buscar por email o negocio..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="email-manager-filter-group">
          <div className="email-manager-filter-tabs">
            <button
              className={filter === "all" ? "active" : ""}
              onClick={() => setFilter("all")}
            >
              📊 Todos
            </button>
            <button
              className={filter === "sent" ? "active" : ""}
              onClick={() => setFilter("sent")}
            >
              ✓ Enviados
            </button>
            <button
              className={filter === "failed" ? "active" : ""}
              onClick={() => setFilter("failed")}
            >
              ❌ Fallidos
            </button>
            <button
              className={filter === "pending" ? "active" : ""}
              onClick={() => setFilter("pending")}
            >
              ⏳ Pendientes
            </button>
          </div>

          <select
            className="email-manager-type-select"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="all">Todos los tipos</option>
            <option value="welcome">Bienvenida</option>
            <option value="agent_ready">Agente Listo</option>
            <option value="payment_confirmation">Confirmación Pago</option>
            <option value="subscription_cancelled">Cancelación</option>
            <option value="custom">Personalizado</option>
          </select>
        </div>
      </div>

      {/* Emails Table */}
      <div className="emails-table-container">
        <table className="emails-table">
          <thead>
            <tr>
              <th>Negocio</th>
              <th>Destinatario</th>
              <th>Asunto</th>
              <th>Tipo</th>
              <th>Estado</th>
              <th>Fecha</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredEmails.length > 0 ? (
              filteredEmails.map((email) => (
                <tr key={email._id}>
                  <td className="business-cell">{email.businessName}</td>
                  <td>
                    <span className="email-manager-email-icon">📧</span>
                    {email.to}
                  </td>
                  <td className="subject-cell">{email.subject}</td>
                  <td>
                    <span className={`email-type ${email.type}`}>
                      {getTypeLabel(email.type)}
                    </span>
                  </td>
                  <td>
                    <span className={`email-status ${email.status}`}>
                      {getStatusLabel(email.status)}
                    </span>
                  </td>
                  <td className="date-cell">
                    {email.sentAt ? (
                      <>
                        <div>
                          {new Date(email.sentAt).toLocaleDateString("es-ES", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </div>
                        <div className="email-manager-time-text">
                          {new Date(email.sentAt).toLocaleTimeString("es-ES", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </div>
                      </>
                    ) : email.createdAt ? (
                      <>
                        <div>
                          {new Date(email.createdAt).toLocaleDateString(
                            "es-ES",
                            {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            }
                          )}
                        </div>
                        <div className="email-manager-time-text">
                          {new Date(email.createdAt).toLocaleTimeString(
                            "es-ES",
                            {
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )}
                        </div>
                      </>
                    ) : (
                      <span className="email-manager-not-sent">-</span>
                    )}
                  </td>
                  <td className="actions-cell">
                    <button
                      className="email-manager-view-btn"
                      onClick={() => handleViewEmail(email)}
                      title="Ver detalles"
                    >
                      👁️ Ver
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="email-manager-empty-row">
                  <div className="email-manager-empty-state">
                    <p>📭 No hay correos en el historial</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Email Details Modal */}
      {selectedEmail && (
        <div className="email-manager-modal-overlay" onClick={handleCloseModal}>
          <div
            className="email-manager-modal-content email-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="email-manager-modal-header">
              <h2>Detalles del Correo</h2>
              <button className="email-manager-close-btn" onClick={handleCloseModal}>
                ✕
              </button>
            </div>

            <div className="email-manager-modal-body">
              <div className="email-manager-detail-section">
                <h3>Información General</h3>
                <div className="email-manager-detail-row">
                  <span className="email-manager-detail-label">Destinatario:</span>
                  <span className="email-manager-detail-value">{selectedEmail.to}</span>
                </div>
                <div className="email-manager-detail-row">
                  <span className="email-manager-detail-label">Negocio:</span>
                  <span className="email-manager-detail-value">
                    {selectedEmail.businessName}
                  </span>
                </div>
                <div className="email-manager-detail-row">
                  <span className="email-manager-detail-label">Asunto:</span>
                  <span className="email-manager-detail-value">{selectedEmail.subject}</span>
                </div>
                <div className="email-manager-detail-row">
                  <span className="email-manager-detail-label">Tipo:</span>
                  <span className={`email-type ${selectedEmail.type}`}>
                    {getTypeLabel(selectedEmail.type)}
                  </span>
                </div>
                <div className="email-manager-detail-row">
                  <span className="email-manager-detail-label">Estado:</span>
                  <span className={`email-status ${selectedEmail.status}`}>
                    {getStatusLabel(selectedEmail.status)}
                  </span>
                </div>
                {selectedEmail.sentAt && (
                  <div className="email-manager-detail-row">
                    <span className="email-manager-detail-label">Enviado:</span>
                    <span className="email-manager-detail-value">
                      {new Date(selectedEmail.sentAt).toLocaleString("es-ES")}
                    </span>
                  </div>
                )}
                {selectedEmail.error && (
                  <div className="email-manager-detail-row error-row">
                    <span className="email-manager-detail-label">Error:</span>
                    <span className="email-manager-detail-value email-manager-error-text">
                      {selectedEmail.error}
                    </span>
                  </div>
                )}
              </div>

              <div className="email-manager-detail-section">
                <h3>Vista Previa del Email</h3>
                <div className="email-manager-email-preview">
                  <iframe
                    srcDoc={selectedEmail.template}
                    title="Email Preview"
                    sandbox="allow-same-origin"
                  />
                </div>
              </div>

              {selectedEmail.status === "failed" && (
                <div className="email-manager-modal-actions">
                  <button
                    className="email-manager-resend-btn"
                    onClick={() => handleResendEmailRequest(selectedEmail._id)}
                    disabled={resending}
                  >
                    {resending ? "⏳ Reenviando..." : "🔄 Reenviar Correo"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {showConfirmModal && (
        <div className="confirm-modal-overlay" onClick={handleCancelResend}>
          <div className="confirm-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-modal-icon">
              <span className="confirm-warning-icon">⚠️</span>
            </div>
            <h2 className="confirm-modal-title">Confirmar Reenvío</h2>
            <p className="confirm-modal-message">
              ¿Estás seguro de que quieres reenviar este correo?
            </p>
            <div className="confirm-modal-actions">
              <button
                className="confirm-btn-cancel"
                onClick={handleCancelResend}
              >
                Cancelar
              </button>
              <button
                className="confirm-btn-confirm"
                onClick={handleConfirmResend}
              >
                🔄 Reenviar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Toast */}
      {notification && (
        <div className={`email-notification ${notification.type}`}>
          <span className="email-manager-notification-icon">
            {notification.type === 'success' ? '✓' : '❌'}
          </span>
          <span className="email-manager-notification-message">{notification.message}</span>
        </div>
      )}

      {/* AI Effects */}
      <div className="email-effects">
        <div className="envelope-particle particle-1">✉️</div>
        <div className="envelope-particle particle-2">📧</div>
        <div className="envelope-particle particle-3">📬</div>
        <div className="glow-orb orb-1"></div>
        <div className="glow-orb orb-2"></div>
      </div>
    </div>
  );
};

export default EmailManager;
